import React from 'react';

import {
    Grid,
    ToggleButton,
    ToggleButtonGroup,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    ReactHookFormIntlNumberInput,
    ReactHookFormSelect,
    ReactHookFormSwitch,
} from 'common/components/form';

import OpexArrayRanges from './OpexArrayRanges';

const OpexFields = ({
    control,
    currencyIso,
    currencySymbol,
    disabled,
    getValues,
    setValue,
}) => {
    const { t } = useTranslation();
    const [opexActive, opexCostingType, opexPeriodicity] = useWatch({
        control,
        name: ['opex_active', 'opex_costing_type', 'opex_periodicity'],
    });
    return (
        <>
            <Grid container>
                <Grid pl={1} size={{ md: 4, xs: 18 }}>
                    <ReactHookFormSwitch
                        control={control}
                        disabled={disabled}
                        fullWidth
                        label={t('Active')}
                        labelPlacement="start"
                        name="opex_active"
                    />
                </Grid>
                <Grid size={{ md: 5, xs: 18 }}>
                    <ReactHookFormSelect
                        control={control}
                        disabled={disabled || !opexActive}
                        fullWidth
                        label={t('Costing type')}
                        name="opex_costing_type"
                        onChange={({ target }) => {
                            if (target.value === 'by_kw_and_kwh')
                                setValue('opex', 0);
                        }}
                        options={[
                            {
                                label: t('Fixed amount'),
                                value: 'by_fixed_amount',
                            },
                            {
                                label: t('Per kW'),
                                value: 'by_kw_and_kwh',
                            },
                        ]}
                    />
                </Grid>
            </Grid>

            <Grid container mt={2}>
                {opexCostingType === 'by_fixed_amount' && (
                    <Grid size={{ md: 6, xs: 18 }}>
                        <ReactHookFormIntlNumberInput
                            allowNegativeValue={false}
                            append={currencyIso}
                            control={control}
                            disabled={disabled || !opexActive}
                            fullWidth
                            label={t('Cost')}
                            min="0"
                            name="opex"
                            prepend={currencySymbol}
                        />
                    </Grid>
                )}
                <Grid
                    size={{
                        md: opexCostingType === 'by_fixed_amount' ? 6 : 9,
                        xs: 18,
                    }}
                >
                    <ReactHookFormIntlNumberInput
                        allowNegativeValue={false}
                        append="%"
                        control={control}
                        disabled={disabled || !opexActive}
                        fullWidth
                        label={t('Annual percentage increase')}
                        min={0}
                        name="opex_percentage_increase"
                        step="any"
                    />
                </Grid>
                <Grid
                    size={{
                        md: opexCostingType === 'by_fixed_amount' ? 6 : 9,
                        xs: 18,
                    }}
                >
                    <ToggleButtonGroup
                        color="primary"
                        disabled={disabled || !opexActive}
                        exclusive
                        fullWidth
                        onChange={(e) =>
                            setValue('opex_periodicity', e.target.value)
                        }
                        value={parseInt(opexPeriodicity)}
                        size="small"
                        sx={{
                            mb: 2,
                            '& .MuiToggleButton-root': {
                                '&.Mui-disabled': {
                                    color: '#b0b0b0',
                                    borderColor: '#b0b0b0',
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                        color: '#b0b0b0',
                                    },
                                },
                            },
                        }}
                    >
                        <ToggleButton value={0}>{t('Monthly')}</ToggleButton>
                        <ToggleButton value={1}>{t('Yearly')}</ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>
            {opexCostingType === 'by_kw_and_kwh' && (
                <Grid container mt={2}>
                    <Grid size={18}>
                        <OpexArrayRanges
                            control={control}
                            currencyIso={'USD'}
                            currencySymbol={'$'}
                            disabled={disabled}
                            getValues={getValues}
                            name="opex_cost_ranges_kw"
                            setValue={setValue}
                            title={t('Cost per kW')}
                        />
                    </Grid>
                </Grid>
            )}
        </>
    );
};

OpexFields.propTypes = {
    control: PropTypes.object,
    currencyIso: PropTypes.string,
    currencySymbol: PropTypes.string,
    disabled: PropTypes.bool,
    getValues: PropTypes.func,
    setValue: PropTypes.func,
};

export default OpexFields;
